import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { readCachedStore } from 'utils/storeProfile';
import id from './id.json';
import en from './en.json';
import {
  LANGUAGE_OG_LOCALES,
  LANGUAGE_STORAGE_KEY,
  normalizeLanguage,
  persistLanguage,
  readStoredLanguage,
  resolveLanguage,
} from './language';

export { LANGUAGES, LANGUAGE_NAMES, localeFor } from './language';

/*
 * Bahasa Indonesia is the default and English is the other UI language.
 * Translations are bundled: no network round trip. Which one is active is
 * decided in ./language.js (visitor's choice > shop locale > Indonesian).
 *
 * The visitor's choice is kept here too, not only in localStorage, so it
 * still wins when storage is unavailable and the shop profile arrives later.
 */
let visitorChoice = readStoredLanguage();

/** The visitor's explicit choice, if any. */
export const getVisitorChoice = () => visitorChoice;

/** The language the shop's `locale` asks for, unless the visitor has chosen. */
export function followShopLocale(shopLocale) {
  if (visitorChoice) return;
  const lang = resolveLanguage({ shopLocale });
  if (lang !== i18n.language) i18n.changeLanguage(lang);
}

/** The visitor picked a language: use it now and remember it. */
export function chooseLanguage(lang) {
  const next = normalizeLanguage(lang);
  if (!next) return;
  visitorChoice = next;
  persistLanguage(next);
  if (next !== i18n.language) i18n.changeLanguage(next);
}

// First paint in the right language: the visitor's choice, else the last
// known shop profile's locale (cached), else Indonesian.
const cached = readCachedStore();

i18n.use(initReactI18next).init({
  resources: { id: { translation: id }, en: { translation: en } },
  lng: resolveLanguage({ choice: visitorChoice, shopLocale: cached && cached.locale }),
  fallbackLng: 'id',
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
});

function syncDocument(lang) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lang;
  // A page served by the SEO Edge Function carries og:locale (Indonesian).
  const og = document.head.querySelector('meta[property="og:locale"]');
  if (og) og.setAttribute('content', LANGUAGE_OG_LOCALES[lang] || LANGUAGE_OG_LOCALES.id);
}

syncDocument(i18n.language);
i18n.on('languageChanged', syncDocument);

// Another tab changed the language: follow it.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== LANGUAGE_STORAGE_KEY) return;
    const lang = normalizeLanguage(e.newValue);
    if (!lang) return;
    visitorChoice = lang;
    if (lang !== i18n.language) i18n.changeLanguage(lang);
  });
}

export default i18n;
